import { useCallback, useId, useRef, useState, type ReactNode } from 'react'
import { Button } from '../../platform/primitives/Button'
import { Panel } from '../../platform/primitives/Panel'
import { MerchantPickerDialog, type PickerMerchant } from '../merchants/picker'
import { usePickedRowFocus } from '../transactions/usePickedRowFocus'
import type { SameDescription } from './sameDescription'
import { useMerchantForRows } from './useMerchantForRows'

/**
 * "N of these say <DESCRIPTION>. Set all N at once?" — on the No merchant page, above the list, for the
 * largest group of rows that share a bank description (`largestSameDescription`). Presentational: the
 * container owns the picker. The description is the bank's raw text, so it is set in monospace as the
 * picker's own header sets it.
 *
 * Outline, not primary: the button is one offer on a page whose task is the list (rule 9 keeps lime
 * for the primary action, and this is not the page's one).
 *
 * `busy` while a set is running (the prompt stays until the refetch takes its rows away): the button is
 * `aria-disabled` and inert, and the sentence it is described by says why, so a second press cannot send
 * a second round of PATCHes with a second Undo (reviewer M3).
 */
export function SameDescriptionPrompt({
  group,
  onChoose,
  busy = false,
}: {
  group: SameDescription
  onChoose: (group: SameDescription, opener: HTMLButtonElement) => void
  busy?: boolean
}) {
  const textId = useId()
  const n = group.rows.length
  return (
    <Panel className="cf-panel cf-same" role="region" aria-label="Set all at once">
      <p id={textId} className="cf-same__text">
        {busy ? (
          `Setting the merchant on all ${n}…`
        ) : (
          <>
            {n} of these say <span className="cf-same__raw">{group.raw}</span>. Set all {n} at once?
          </>
        )}
      </p>
      <Button
        type="button"
        variant="outline"
        size="panel"
        className="cf-same__action"
        aria-describedby={textId}
        aria-disabled={busy || undefined}
        onClick={(e) => {
          if (!busy) onChoose(group, e.currentTarget)
        }}
      >
        Choose merchant
      </Button>
    </Panel>
  )
}

/**
 * The prompt's picker: one dialog, which sets the merchant on every row of the group the prompt named
 * when it was opened (`useMerchantForRows`: per-row PATCH, one Undo). The rows then leave the No
 * merchant list on refetch, and the prompt with them, so focus is watched as a row pill's is.
 *
 * `busy` from the pick until the set settles; `onChoose` refuses while it is, whatever the button says.
 */
export function useSameDescriptionPick(): {
  onChoose: (group: SameDescription, opener: HTMLButtonElement) => void
  dialog: ReactNode
  busy: boolean
} {
  const [group, setGroup] = useState<SameDescription | null>(null)
  const [busy, setBusy] = useState(false)
  const opener = useRef<HTMLElement | null>(null)
  const watch = usePickedRowFocus()
  const many = useMerchantForRows()

  const onChoose = useCallback(
    (g: SameDescription, el: HTMLButtonElement) => {
      if (busy) return
      opener.current = el
      setGroup(g)
    },
    [busy],
  )

  const dialog = (
    <MerchantPickerDialog
      open={group !== null}
      onClose={() => setGroup(null)}
      value={null}
      onPick={(id, merchant) => {
        // No "Remove merchant" is offered (none of these rows has one), so a null pick cannot happen.
        if (group === null || id === null) return
        const next: PickerMerchant = merchant ?? { id, name: 'the new merchant', identity: { id, name: 'the new merchant', type: 'business', is_global: false } }
        setBusy(true)
        const done = many.set(group.rows, next).finally(() => setBusy(false))
        watch(opener.current, done)
      }}
      restoreFocusTo={opener}
    />
  )
  return { onChoose, dialog, busy }
}
